import React, { useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  ButtonBase,
  Button as MuiButton,
  TextField,
} from '@mui/material';
import { ArrowRight, Video, Upload, HelpCircle } from 'lucide-react';
import { toast } from '../../../../../ui/toast';
import { submitPreScreenAnswers } from '../../../../../utils/api_service/assessment_api';
import { useGetPreQualVideoPresignMutation } from '../../../../../utils/api_service/preQualApi';
import {
  PreScreenAnswers,
  PreScreenQuestion,
  UserAssessment,
} from '../../../../../utils/types/assessment_types';

interface PreScreenPerformProps {
  preScreenQuestions: PreScreenQuestion[];
  selectedUserAssessment: UserAssessment | undefined;
  finishedPreScreen: (userAssessment: UserAssessment) => void;
}

export const PreScreenPerform: React.FC<PreScreenPerformProps> = ({
  preScreenQuestions,
  selectedUserAssessment,
  finishedPreScreen,
}) => {
  const [preScreenAnswers, setPreScreenAnswers] = useState<PreScreenAnswers[]>(
    [],
  );
  const [videoAnswers, setVideoAnswers] = useState<
    Record<string, { file: File; previewUrl: string }>
  >({});
  const [getVideoPresign] = useGetPreQualVideoPresignMutation();

  const handleChangeAnswer = (
    questionId: string,
    questionType: PreScreenQuestion['questionType'],
    answer: string | number,
    videoKey?: string,
  ) => {
    let transformedAnswer: string | number;
    if (questionType === 'numeric') {
      transformedAnswer = parseInt(answer as string, 10);
    } else {
      transformedAnswer = answer.toString();
    }

    setPreScreenAnswers((prevAnswers) => {
      const existingAnswerIndex = prevAnswers.findIndex(
        (ans) => ans.questionId === questionId,
      );
      if (existingAnswerIndex !== -1) {
        const updatedAnswers = [...prevAnswers];
        updatedAnswers[existingAnswerIndex] = {
          questionId,
          answer: transformedAnswer,
          questionType: questionType,
          ...(videoKey && { videoKey }),
        };
        return updatedAnswers;
      } else {
        return [
          ...prevAnswers,
          {
            questionId,
            answer: transformedAnswer,
            questionType: questionType,
            ...(videoKey && { videoKey }),
          },
        ];
      }
    });
  };

  const handleVideoAnswer = (questionId: string, file: File) => {
    setVideoAnswers((prev) => {
      if (prev[questionId]?.previewUrl) {
        URL.revokeObjectURL(prev[questionId].previewUrl);
      }

      return {
        ...prev,
        [questionId]: {
          file,
          previewUrl: URL.createObjectURL(file),
        },
      };
    });
    handleChangeAnswer(questionId, 'video', '');
  };

  const uploadVideoAnswer = async (questionId: string) => {
    const videoAnswer = videoAnswers[questionId];
    if (!videoAnswer) return '';

    const presign = await getVideoPresign({ questionKey: questionId }).unwrap();
    if (!presign.url) {
      throw new Error('Missing upload URL');
    }

    const uploadResponse = await fetch(presign.url, {
      method: 'PUT',
      headers: {
        'Content-Type': videoAnswer.file.type || 'video/webm',
      },
      body: videoAnswer.file,
    });

    if (!uploadResponse.ok) {
      throw new Error('Video upload failed');
    }

    return presign.videoKey || presign.url;
  };

  const getAnswerValue = (questionId: string) => {
    const answerObj = preScreenAnswers.find(
      (ans) => ans.questionId === questionId,
    );
    return answerObj ? answerObj.answer : '';
  };

  const validateAnswers = () => {
    for (const question of preScreenQuestions) {
      const answerObj = preScreenAnswers.find(
        (ans) => ans.questionId === question.questionId,
      );
      if (
        !answerObj ||
        (question.questionType !== 'video' && answerObj.answer === '') ||
        (question.questionType === 'video' && !videoAnswers[question.questionId])
      ) {
        return false;
      }
    }
    return true;
  };

  const submitAnswers = async () => {
    if (
      selectedUserAssessment &&
      selectedUserAssessment.assessment &&
      selectedUserAssessment.assessmentUser
    ) {
      try {
        const answersWithVideos = await Promise.all(
          preScreenAnswers.map(async (answer) => {
            if (answer.questionType !== 'video') return answer;
            return {
              ...answer,
              videoKey: await uploadVideoAnswer(answer.questionId),
            };
          }),
        );
        const response = await submitPreScreenAnswers(
          selectedUserAssessment.assessment.id,
          selectedUserAssessment.assessmentUser.id,
          answersWithVideos,
        );
        const responseUserAssessment: UserAssessment = await response.json();
        finishedPreScreen(responseUserAssessment);
      } catch {
        toast.error('Submitting Error. Try Again', {
          position: 'bottom-center',
        });
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 768, py: 4 }}>
        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 4 }}>
          <Box
            sx={{
              width: 6,
              height: 28,
              borderRadius: 3,
              bgcolor: 'success.main',
            }}
          />
          <Typography variant='h5' fontWeight={700} color='text.primary'>
            Pre-Screening Assessment
          </Typography>
        </Stack>

        <Stack spacing={3}>
          {preScreenQuestions.length > 0 ? (
            preScreenQuestions.map((question, indexNum) => (
              <Paper
                key={question.questionId || indexNum}
                variant='outlined'
                sx={{
                  p: 3,
                  borderRadius: 3,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'success.light',
                  },
                }}
              >
                <Stack direction='row' spacing={2} sx={{ mb: 2.5 }} alignItems='flex-start'>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      bgcolor: 'success.50',
                      color: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 13,
                      border: '1px solid',
                      borderColor: 'success.light',
                      flexShrink: 0,
                    }}
                  >
                    {indexNum + 1}
                  </Box>
                  <Typography variant='subtitle1' fontWeight={600} color='text.primary'>
                    {question.question}
                  </Typography>
                </Stack>

                {question.questionType === 'multiple-choice' ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                      pl: 4.5,
                    }}
                  >
                    {question.choices?.map((choice, idx) => {
                      const isSelected =
                        getAnswerValue(question.questionId).toString() ===
                        choice.toString();
                      return (
                        <ButtonBase
                          key={idx}
                          component='div'
                          onClick={() =>
                            handleChangeAnswer(
                              question.questionId,
                              question.questionType,
                              choice,
                            )
                          }
                          sx={{
                            width: '100%',
                            textAlign: 'left',
                            borderRadius: 2,
                            display: 'block',
                          }}
                        >
                          <Paper
                            variant='outlined'
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              borderColor: isSelected ? 'success.main' : 'divider',
                              bgcolor: isSelected ? 'success.50' : 'background.paper',
                              borderWidth: isSelected ? 2 : 1,
                              transition: 'all 0.15s ease-in-out',
                              '&:hover': {
                                borderColor: 'success.light',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                border: '2px solid',
                                borderColor: isSelected ? 'success.main' : 'divider',
                                bgcolor: isSelected ? 'success.main' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {isSelected && (
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'common.white',
                                  }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant='body2'
                              fontWeight={isSelected ? 600 : 400}
                              color={isSelected ? 'success.dark' : 'text.primary'}
                            >
                              {choice}
                            </Typography>
                          </Paper>
                        </ButtonBase>
                      );
                    })}
                  </Box>
                ) : question.questionType === 'video' ? (
                  <Stack spacing={2} sx={{ pl: 4.5 }}>
                    {videoAnswers[question.questionId]?.previewUrl && (
                      <Box
                        component='video'
                        src={videoAnswers[question.questionId].previewUrl}
                        controls
                        sx={{
                          width: '100%',
                          maxWidth: 440,
                          borderRadius: 2,
                          bgcolor: 'common.black',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    )}
                    <MuiButton
                      variant='outlined'
                      component='label'
                      startIcon={videoAnswers[question.questionId] ? <Upload size={16} /> : <Video size={16} />}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {videoAnswers[question.questionId]
                        ? 'Replace Video'
                        : 'Upload Video Answer'}
                      <input
                        type='file'
                        accept='video/*'
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleVideoAnswer(question.questionId, file);
                        }}
                      />
                    </MuiButton>
                  </Stack>
                ) : (
                  <Box sx={{ pl: 4.5, maxWidth: 240 }}>
                    <TextField
                      name={`answer-${question.questionId}`}
                      value={getAnswerValue(question.questionId).toString()}
                      type='number'
                      size='small'
                      placeholder='0'
                      fullWidth
                      onChange={(e) =>
                        handleChangeAnswer(
                          question.questionId,
                          question.questionType,
                          e.target.value,
                        )
                      }
                    />
                  </Box>
                )}
              </Paper>
            ))
          ) : (
            <Paper
              variant='outlined'
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 3,
              }}
            >
              <Stack spacing={1.5} alignItems='center'>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <HelpCircle size={28} />
                </Box>
                <Typography variant='body1' color='text.secondary'>
                  No pre-screening questions found.
                </Typography>
              </Stack>
            </Paper>
          )}
        </Stack>

        <Box sx={{ mt: 5, display: 'flex', justifyContent: 'flex-end' }}>
          <MuiButton
            onClick={() => submitAnswers()}
            variant='contained'
            color='success'
            size='large'
            disabled={!validateAnswers()}
            endIcon={<ArrowRight size={18} />}
            sx={{ px: 4, py: 1.25, borderRadius: 2 }}
          >
            Continue to Assessment
          </MuiButton>
        </Box>
      </Box>
    </Box>
  );
};

export default PreScreenPerform;
